<template>
    <div class="mb-2 mx-4">
        <v-row>
            <v-col cols="12">
              <WarningAlert>
                <p>{{ $t("registeredMembers.description_1") }}</p> 
              </WarningAlert>
            </v-col>
        </v-row>
        <v-row>
            <v-col md="6" sm="8" cols="12">
                <v-autocomplete
                    v-model="organizationSelect"
                    :items="organizations"
                    outlined
                    dense
                    :loading="isLoading"
                    :search-input.sync="search"
                    hide-no-data
                    hide-selected
                    :label="$t('registeredMembers.search_organization')"
                    persistent-hint
                    :hint="$t('registeredMembers.hint_search_organization')"
                    @change="getList"
                    @click="clickSearch"
                    @blur="clickSearch"
                    return-object
                    :filter="customFilter"
                >
                    <template v-slot:item="data">
                        <v-list-item-content >
                            <v-list-item-title v-html="paintMatchingTerms(data.item.text, (search || '').split(' ').filter(obj => obj))"></v-list-item-title>
                        </v-list-item-content>
                    </template>
              </v-autocomplete>
            </v-col>
        </v-row>
        <v-row>
            <v-col class="contentRespTableFilter" cols="12">
                <v-data-table
                    :headers="registeredHeaders"
                    dense
                    :items="filteredItems"
                    item-key="regId"
                    :footer-props="{ 
                      itemsPerPageOptions: [25, 50, 75, 100, -1],
                      'items-per-page-text': $t('membership.items_per_page_text'),
                      'items-per-page-all-text': $t('dataTable.rowsPerPageAllText')
                    }"
                    :items-per-page="itemPerPage"
                    :custom-sort="customSort"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc"
                    class="elevation-1 pa-6"
                    :loading="loadingData"
                    :loading-text="$t('yourRegistration.loading_text')"
                    :no-data-text="$t('membership.no_data_available')"
                    :headerProps="{
                      'sort-by-text': $t('dataTable.sortByText')
                    }"
                >
                    <template v-slot:top>
                      <v-row no-gutters v-if="organizationSelect">
                        <v-col cols="12" class="pb-1">
                          <p class="ml-2 my-0">
                            {{ organizationSelect.promoterName }} - 
                            {{ organizationSelect.startDate | formatDateForHumans(false) }} {{ $t('common.to') }} {{ organizationSelect.endDate | formatDateForHumans(false) }}
                          </p>
                        </v-col>
                        <v-col md="8" sm="10" cols="12" v-if="results.length == 0 && bandReset">
                          <WarningAlert>
                            <p>{{$t("registeredMembers.not_participants_for_org")}}</p> 
                          </WarningAlert>
                        </v-col>
                      </v-row>
                    </template>
                    <template v-slot:header="{ props: { headers } }">
                        <thead class="headTable">
                            <tr>
                                <th v-for="h in headers" :key="h.text" @click="sortableCustom(h.value)" style="cursor: pointer">{{ h.text }}</th>
                            </tr>
                            <tr>
                                <td><v-text-field class="my-2" hide-details outlined dense v-model="raceNumberFilterValue" type="text" :label="$t('registeredMembers.race_number')"></v-text-field></td>
                                <td>
                                  <v-select 
                                    class="my-2 classFilter" 
                                    v-model="orgClassFilterValue" 
                                    :items="itemsOrgClass" outlined multiple dense 
                                    :label="$t('registeredMembers.orgClassName')"
                                    :no-data-text="$t('common.no_data')"
                                  >
                                      <template v-slot:selection="{ item, index }">
                                        <v-chip v-if="index === 0">
                                          <span>{{ item }}</span>
                                        </v-chip>
                                        <span
                                          v-if="index === 1"
                                          class="grey--text caption"
                                        >
                                          (+{{ orgClassFilterValue.length - 1 }} {{ $t("common.others") }})
                                        </span>
                                      </template>
                                  </v-select>
                                </td>
                                <td>
                                  <v-select 
                                    class="my-2 classFilter" 
                                    v-model="classFilterValue" 
                                    :items="itemsClass" outlined multiple dense 
                                    :label="$t('registeredMembers.className')"
                                    :no-data-text="$t('common.no_data')"
                                  >
                                      <template v-slot:selection="{ item, index }">
                                        <v-chip v-if="index === 0">
                                          <span>{{ item }}</span>
                                        </v-chip>
                                        <span
                                          v-if="index === 1"
                                          class="grey--text caption"
                                        >
                                          (+{{ classFilterValue.length - 1 }} {{ $t("common.others") }})
                                        </span>
                                      </template>
                                  </v-select>
                                </td>
                                <td><v-text-field class="my-2" hide-details outlined dense v-model="firstNameFilterValue" type="text" :label="$t('registeredMembers.first_name')"></v-text-field></td>
                                <td><v-text-field class="my-2" hide-details outlined dense v-model="lastNameFilterValue" type="text" :label="$t('registeredMembers.last_name')"></v-text-field></td>
                                <td><v-text-field class="my-2" hide-details outlined dense v-model="createdDateFilterValue" type="text" :label="$t('registeredMembers.created_date')"></v-text-field></td>
                            </tr>
                        </thead>
                        <thead class="headTableMobile">
                            <tr>
                                <td class="pa-0">
                                  <v-expansion-panels class="my-2">
                                    <v-expansion-panel>
                                      <v-expansion-panel-header>{{$t('common.filters')}}</v-expansion-panel-header>
                                      <v-expansion-panel-content>
                                        <v-row>
                                          <v-col cols="12" sm="6" class="py-0">
                                            <v-text-field class="my-2" hide-details outlined dense v-model="raceNumberFilterValue" type="text" :label="$t('registeredMembers.race_number')"></v-text-field>
                                          </v-col>
                                          <v-col cols="12" sm="6" class="py-0">
                                            <v-select 
                                              class="my-2 classFilter" 
                                              v-model="orgClassFilterValue" 
                                              :items="itemsOrgClass" outlined multiple dense 
                                              :label="$t('registeredMembers.orgClassName')"
                                              :no-data-text="$t('common.no_data')"
                                            >
                                                <template v-slot:selection="{ item, index }">
                                                  <v-chip v-if="index === 0">
                                                    <span>{{ item }}</span>
                                                  </v-chip>
                                                  <span
                                                    v-if="index === 1"
                                                    class="grey--text caption"
                                                  >
                                                    (+{{ orgClassFilterValue.length - 1 }} {{ $t("common.others") }})
                                                  </span>
                                                </template>
                                            </v-select>
                                          </v-col>
                                          <v-col cols="12" sm="6" class="py-0">
                                            <v-select 
                                              class="my-2 classFilter" 
                                              v-model="classFilterValue" 
                                              :items="itemsClass" outlined multiple dense 
                                              :label="$t('registeredMembers.className')"
                                              :no-data-text="$t('common.no_data')"
                                            >
                                                  <template v-slot:selection="{ item, index }">
                                                    <v-chip v-if="index === 0">
                                                      <span>{{ item }}</span>
                                                    </v-chip>
                                                    <span
                                                      v-if="index === 1"
                                                      class="grey--text caption"
                                                    >
                                                      (+{{ classFilterValue.length - 1 }} {{ $t("common.others") }})
                                                    </span>
                                                  </template>
                                              </v-select>
                                          </v-col>
                                          <v-col cols="12" sm="6" class="py-0">
                                            <v-text-field class="my-2" hide-details outlined dense v-model="firstNameFilterValue" type="text" :label="$t('registeredMembers.first_name')"></v-text-field>
                                          </v-col>
                                          <v-col cols="12" sm="6" class="py-0">
                                            <v-text-field class="my-2" hide-details outlined dense v-model="lastNameFilterValue" type="text" :label="$t('registeredMembers.last_name')"></v-text-field>
                                          </v-col>
                                          <v-col cols="12" sm="6" class="py-0">
                                            <v-text-field class="my-2" hide-details outlined dense v-model="createdDateFilterValue" type="text" :label="$t('registeredMembers.created_date')"></v-text-field>
                                          </v-col>
                                        </v-row>
                                      </v-expansion-panel-content>
                                    </v-expansion-panel>
                                  </v-expansion-panels>
                                </td>
                            </tr>
                        </thead>
                    </template>
                    <template v-slot:item.createdDate="{ item }">
                      <span>{{ item.createdDate | formatDateForHumans(true, true, true) }}</span>
                  </template>
                </v-data-table>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import { 
  VRow, VCol, VAutocomplete, VDataTable, VTextField, VSelect, VChip, VExpansionPanels, VExpansionPanel, VExpansionPanelHeader, VExpansionPanelContent, VListItemContent, VListItemTitle 
} from 'vuetify/lib';
import WarningAlert from "@/components/alert/WarningAlert";
import RegisteredMembersService from "@/services/RegisteredMembersService.js";
import { formatDateForHumans } from '@/helpers/date-helper';

export default {
  components: {
    VRow, VCol, WarningAlert, VAutocomplete, VListItemContent, VListItemTitle, VDataTable, VTextField, VSelect, VChip, VExpansionPanels, VExpansionPanel, VExpansionPanelHeader, VExpansionPanelContent
  },
  data() {
    return {
        organizationSelect: null,
        organizations: [],
        isLoading: false,
        search: '',
        sortBy: null,
        sortDesc: false,
        lastSearch: '',
        loadingData: false,
        itemPerPage: 25,
        raceNumberFilterValue: '',
        orgClassFilterValue: [],
        classFilterValue: [],
        itemsClass: [],
        itemsOrgClass: [],
        firstNameFilterValue: '',
        lastNameFilterValue: '',
        createdDateFilterValue: '',
        results: [],
        bandReset: false
    };
  },
  computed: {
    registeredHeaders() {
        return [
            { text: this.$t("registeredMembers.race_number"), value: "number" },
            { text: this.$t("registeredMembers.orgClassName"), value: "orgClassName" },
            { text: this.$t("registeredMembers.className"), value: "className" },
            { text: this.$t("registeredMembers.first_name"), value: "firstName" },
            { text: this.$t("registeredMembers.last_name"), value: "lastName" },
            { text: this.$t("registeredMembers.created_date"), value: "createdDate" }
        ];
    },
    filteredItems() {
      let results = [], vm = this
      vm.sortBy = "createdDate";
      vm.sortDesc = false;
      if(this.results.length > 0) {
        results = this.results;
          if(
              (this.raceNumberFilterValue || "").length > 0 || (this.orgClassFilterValue || "").length > 0 || 
              (this.classFilterValue || "").length > 0 || (this.firstNameFilterValue || "").length > 0 || 
              (this.lastNameFilterValue || "").length > 0 || (this.createdDateFilterValue || "").length > 0
            ) {
              if(this.raceNumberFilterValue != ''  ){
                vm.sortBy = "number";
                vm.sortDesc = true;
              }
            results = results.filter((item) => {
              return (
                (
                  (this.raceNumberFilterValue || "").length == 0 || 
                  ((this.raceNumberFilterValue || "").length > 0 && item.number.toLowerCase().includes(this.raceNumberFilterValue.toLowerCase()))
                ) && 
                (
                  (this.orgClassFilterValue || "").length == 0 ||
                  (this.orgClassFilterValue || "").length > 0 && this.orgClassFilterValue.some(items => item.orgClassName.toLowerCase() === items.toLowerCase())
                ) && 
                (
                  (this.classFilterValue || "").length == 0 ||
                  (this.classFilterValue || "").length > 0 && this.classFilterValue.some(items => item.className.toLowerCase() === items.toLowerCase())
                ) && 
                (
                  (this.firstNameFilterValue || "").length == 0 || 
                  (this.firstNameFilterValue || "").length > 0 && item.firstName.toLowerCase().includes(this.firstNameFilterValue.toLowerCase())
                ) && 
                (
                  (this.lastNameFilterValue ||  "").length == 0 || 
                  (this.lastNameFilterValue ||  "").length > 0 && item.lastName.toLowerCase().includes(this.lastNameFilterValue.toLowerCase())
                ) && 
                (
                  (this.createdDateFilterValue || "").length == 0 ||
                  ((this.createdDateFilterValue || "").length > 0 && 
                  this.$options.filters.formatDateForHumans(item.createdDate, true, true, true).toLowerCase().includes(this.createdDateFilterValue.toLowerCase()))
                )
              )
            });
          }
      }
      return results;
    }
  },
  methods: {
    customFilter (item, queryText) {
          const arraySearch = queryText.toLowerCase().replaceAll("\\","\\\\")
                                        .replaceAll("(", "\\(")
                                        .replaceAll(")", "\\)")
                                        .replaceAll(".", "\\.")
                                        .replaceAll("*", "\\*")
                                        .replaceAll("+", "\\+")
                                        .replaceAll("?", "\\?")
                                        .replaceAll("^", "\\^")
                                        .replaceAll("$", "\\$")
                                        .replaceAll("[", "\\]")
                                        .replaceAll("]", "\\]")
                                        .replaceAll("}", "\\}")
                                        .replaceAll("{", "\\{")
                                        .replaceAll("|", "\\|").split(' ');
          const regex = new RegExp(arraySearch.join('|'));
          
          return item.text.toLowerCase().search(regex) > -1
    },
    getList(itemSelected) {
        if(itemSelected) {
            this.loadingData = true;
            RegisteredMembersService.getList(itemSelected.perId).then(response => {
                if(response.data.success) {
                  this.results = response.data.message;
                  this.loadingData = false;
                  this.bandReset = true;
                }
                
            });
        }
    },
    clickSearch() {
        this.search = this.lastSearch;
    },
    searchCacheHandler(search) {
        /**
         * Store data in the search cache
         * only what the user writes will be cached here
         */
        if(search){
    
            if(search.length == 1 && !this.lastSearch) {
                this.lastSearch = search;
            }
    
            if(this.lastSearch &&  this.lastSearch.length == (search.length - 1)) {
                this.lastSearch = search;
            }
    
            if(this.lastSearch &&  this.lastSearch.length == (search.length + 1)) {
                this.lastSearch = search;
            }
        }
    
        if(!search && this.lastSearch &&  this.lastSearch.length == 1)
        {
            this.lastSearch = search;
        }
    },
    paintMatchingTerms(str, terms) {
            if(!terms || terms.length === 0) {
                return str;
            }
            let response = str;
            let expReg = terms.map((term) => {
                return term.replaceAll("\\","\\\\")
                    .replaceAll("(", "\\(")
                    .replaceAll(")", "\\)")
                    .replaceAll(".", "\\.")
                    .replaceAll("*", "\\*")
                    .replaceAll("+", "\\+")
                    .replaceAll("?", "\\?")
                    .replaceAll("^", "\\^")
                    .replaceAll("$", "\\$")
                    .replaceAll("[", "\\]")
                    .replaceAll("]", "\\]")
                    .replaceAll("}", "\\}")
                    .replaceAll("{", "\\{")
                    .replaceAll("|", "\\|");
            }).join('|');
                        
            let regex = new RegExp(expReg,"gi");
            response = str.replaceAll( regex, function(value) { return `<span style="color: #808080;">${value}</span>`; });

            return response;
    },
    customSort(items, sortBy, sortDesc) {
      let results = items;
      if(sortBy && sortBy.length > 0 && sortDesc && sortDesc.length > 0) {
        let reA = /[^a-zA-Z]/g;
        let reN = /[^0-9]/g;
        
        if(sortBy[0] == "number") {
          results = items.sort( function (a, b) {
            var AInt = parseInt(a[sortBy[0]], 10);
            var BInt = parseInt(b[sortBy[0]], 10);

            if(isNaN(AInt) && isNaN(BInt)){
                var aA = a[sortBy[0]].replace(reA, "");
                var bA = b[sortBy[0]].replace(reA, "");
                if(aA === bA) {
                    var aN = parseInt(a[sortBy[0]].replace(reN, ""), 10);
                    var bN = parseInt(b[sortBy[0]].replace(reN, ""), 10);
                    return aN === bN ? 0 : aN > bN ? 1 : -1;
                } else {
                    return aA > bA ? 1 : -1;
                }
            }else if(isNaN(AInt)){//A is not an Int
                return 1;//to make alphanumeric sort first return -1 here
            }else if(isNaN(BInt)){//B is not an Int
                return -1;//to make alphanumeric sort first return 1 here
            }else{
                return AInt > BInt ? 1 : -1;
            }
          });
        } else {
          results = items.sort( function (x, y){
            let _x = x[sortBy[0]];
            let _y = y[sortBy[0]];
            if(sortBy[0] == "class" || sortBy[0] == "firstName" || sortBy[0] == "lastName" || sortBy[0] == "status") {
              _x = x[sortBy[0]].toLowerCase();
              _y = y[sortBy[0]].toLowerCase();
              _x = _x.trim();
              _y = _y.trim();
            } else {
              _x = new Date(x[sortBy[0]]);
              _y = new Date(y[sortBy[0]]);
            }
            if (_x < _y) {return -1;}
            if (_x > _y) {return 1;}
            return 0;
          });
        }
        
        if(sortDesc[0]) {
          results = results.reverse();
        }
        if(sortBy[0] == "number") {
          let nullResults = results.filter((el) => { return !el.number || el.number == "" });
          let notNullResults = results.filter((el) => { return el.number && el.number != "" });
          results = notNullResults.concat(nullResults);
        }

        return results;
      }
      return items;
    },
    getClasses(results) {
      let _class = [], _orgClass = [];
      if(results && Array.isArray(results)) {
        results.forEach((el) => {
          const find = _class.find((el1) => el1 == el.className);
          if(!find) {
            _class.push(el.className.trim());
          }
        });
        results.forEach((el) => {
          const find = _orgClass.find((el1) => el1 == el.orgClassName);
          if(!find) {
            _orgClass.push(el.orgClassName.trim());
          }
        });
      }
      this.itemsClass = _class.sort();
      this.itemsOrgClass = _orgClass.sort();
    },
    sortableCustom(item) {
      if(item != this.sortBy) {
        this.sortBy = item;
        this.sortDesc = false;
      } else {
        this.sortDesc = !this.sortDesc;
      }
    },
  },
  watch: {
    async search(val) {

        this.searchCacheHandler(val);

        if((val || "").length >= 3) {
            if (this.isLoading) return
            this.isLoading = true
            RegisteredMembersService.findOrganizations({ search: this.search.toLowerCase() }).then(response => {
                this.organizations = response.data.message.map((item) => {
                    return {
                        ...item,
                        value: item.perId,
                        text: `${item.promoterName} - ${formatDateForHumans(item.startDate,false)} to ${formatDateForHumans(item.endDate,false)}`
                    };
                });
                this.isLoading = false;
                this.sortBy = "createdDate";
                this.sortDesc = false;
            });
        } else {
            this.isLoading = false;
            this.organizations = [];
        }
    },
    results(val) {
      this.getClasses(val);
    }
  }
};
</script>

<style>
.btn{
  color: white !important;
}
.headTable tr:first-child { background: #ccc !important; }
.headTable tr td { border-bottom: 1px solid #ccc; }
.headTable tr:first-child th:hover {
    color: rgba(0, 0, 0, .8) !important;
}
@media (min-width: 600px) {
    .contentRespTableFilter .v-data-table-header, .contentRespTableFilter .headTableMobile {
        display: none;
    }
}
@media (max-width: 599px) {
  .contentRespTableFilter .headTable, .contentRespTableFilter .v-data-table-header-mobile {
        display: none;
  }
  .confirmedOpenRsp.v-card__actions {
        display: block;
  }
  .btn_bg, .btn_xs_rsp {
    max-width: 100%;
    min-width: auto !important;
    height: auto !important;
  }
  .btn_bg span, .btn_xs_rsp span {
    white-space: normal;
    max-width: 100%;
  }
  .lbl_search .v-text-field__details, .lbl_search .v-text-field__details .v-messages {
    min-height: 0px;
    margin-bottom: 0px !important;
  }
  .btn_bg {
    min-height: 50px;
  }
  .btn_xs_rsp {
    min-height: 36px;
  }
}
.classFilter .v-text-field__details {
  display: none;
}
.classFilter .v-input__slot {
  margin-bottom: 0px !important;
}

.v-chip.v-size--default {
    font-size: 12px !important;
    height: 26px !important;
}
</style>